import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

// import axios from "../../../api/axios";
import axiosInstance from "../../../api/axios";

import { IndiaMap } from "@vishalvoid/react-india-map";

import { Form, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
    Shop,
    ShopWindow,
    CheckCircleFill,
    XCircleFill,
    ClockFill,
    Clock,
    TelephoneFill,
    ChevronRight,
    ArrowLeft,
    GeoAltFill,
} from "react-bootstrap-icons";

/* ==========================================================================
   STATE MAPPING
   ========================================================================== */

const STATE_ID_MAP = {
    "Andhra Pradesh": "IN-AP",
    "Arunachal Pradesh": "IN-AR",
    Assam: "IN-AS",
    Bihar: "IN-BR",
    Chhattisgarh: "IN-CT",
    Goa: "IN-GA",
    Gujarat: "IN-GJ",
    Haryana: "IN-HR",
    "Himachal Pradesh": "IN-HP",
    Jharkhand: "IN-JH",
    Karnataka: "IN-KA",
    Kerala: "IN-KL",
    "Madhya Pradesh": "IN-MP",
    Maharashtra: "IN-MH",
    Manipur: "IN-MN",
    Meghalaya: "IN-ML",
    Mizoram: "IN-MZ",
    Nagaland: "IN-NL",
    Odisha: "IN-OR",
    Punjab: "IN-PB",
    Rajasthan: "IN-RJ",
    Sikkim: "IN-SK",
    "Tamil Nadu": "IN-TN",
    Telangana: "IN-TG",
    Tripura: "IN-TR",
    "Uttar Pradesh": "IN-UP",
    Uttarakhand: "IN-UT",
    "West Bengal": "IN-WB",

    "Andaman and Nicobar": "IN-AN",
    Chandigarh: "IN-CH",
    "Dadra and Nagar Haveli and Daman and Diu": "IN-DN",
    Delhi: "IN-DL",
    "Jammu and Kashmir": "IN-JK",
    Ladakh: "IN-LA",
    Lakshadweep: "IN-LD",
    Puducherry: "IN-PY",
};

/* ==========================================================================
   HEATMAP COLORS
   ========================================================================== */

const HEAT_COLORS = {
    empty: "#EEF4F5",
    veryLow: "#DDF4F3",
    low: "#A9E1DF",
    medium: "#67C6C3",
    high: "#2BA7A4",
    veryHigh: "#087C7A",
};

/* ==========================================================================
   STATUS META
   ========================================================================== */

const STATUS_META = {
    open: {
        label: "Open",
        className: "open",
    },

    closed: {
        label: "Closed",
        className: "closed",
    },

    temporarily_closed: {
        label: "Temp. Closed",
        className: "temporary",
    },
};

const DATE_FILTER_OPTIONS = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last7days" },
    { label: "Last 30 Days", value: "last30days" },
    { label: "This Month", value: "thisMonth" },
    { label: "Last Month", value: "lastMonth" },
    { label: "Custom Range", value: "custom" },
];

const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 91);
    return date;
};

const toApiDate = (date) => date.toISOString().split("T")[0];

/* ==========================================================================
   HELPERS
   ========================================================================== */

const timeToMinutes = (time) => {
    if (!time) return null;

    const [hours, minutes] = String(time).split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
    }

    return hours * 60 + minutes;
};

/* -------------------------------------------------------------------------- */

const getCurrentMinutes = (outlet) => {
    if (outlet?.currentLocalDate) {
        const currentDate = new Date(outlet.currentLocalDate);

        if (!Number.isNaN(currentDate.getTime())) {
            return (
                currentDate.getUTCHours() * 60 +
                currentDate.getUTCMinutes()
            );
        }
    }

    const now = new Date();

    return now.getHours() * 60 + now.getMinutes();
};

/* -------------------------------------------------------------------------- */

const getOutletStatus = (outlet) => {
    /*
     * Temporarily closed has highest priority.
     */

    if (outlet?.isOutletClosed === true) {
        return "temporarily_closed";
    }

    /*
     * Missing operating hours.
     */

    if (!outlet?.openingTime || !outlet?.closingTime) {
        return "closed";
    }

    const openingMinutes = timeToMinutes(outlet.openingTime);
    const closingMinutes = timeToMinutes(outlet.closingTime);
    const currentMinutes = getCurrentMinutes(outlet);

    if (
        openingMinutes === null ||
        closingMinutes === null
    ) {
        return "closed";
    }

    /*
     * Same opening + closing time = 24 hours.
     */

    if (openingMinutes === closingMinutes) {
        return "open";
    }

    /*
     * Normal hours.
     *
     * Example:
     * 08:00 -> 23:00
     */

    if (closingMinutes > openingMinutes) {
        return (
            currentMinutes >= openingMinutes &&
            currentMinutes < closingMinutes
        )
            ? "open"
            : "closed";
    }

    /*
     * Overnight hours.
     *
     * Example:
     * 18:00 -> 02:00
     */

    return (
        currentMinutes >= openingMinutes ||
        currentMinutes < closingMinutes
    )
        ? "open"
        : "closed";
};

/* -------------------------------------------------------------------------- */

const formatOrders = (value) => {
    return Number(value || 0).toLocaleString("en-IN");
};

/* -------------------------------------------------------------------------- */

const formatRevenue = (value) => {
    const amount = Number(value || 0);

    if (amount >= 10000000) {
        return `₹${(amount / 10000000).toFixed(1)} Cr`;
    }

    if (amount >= 100000) {
        return `₹${(amount / 100000).toFixed(1)} L`;
    }

    return `₹${amount.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    })}`;
};

/* -------------------------------------------------------------------------- */

const formatTime = (time) => {
    if (!time) {
        return "-";
    }

    const [hour, minute] = String(time)
        .split(":")
        .map(Number);

    if (
        Number.isNaN(hour) ||
        Number.isNaN(minute)
    ) {
        return time;
    }

    const suffix = hour >= 12 ? "PM" : "AM";

    const formattedHour = hour % 12 || 12;

    return `${formattedHour}:${String(minute).padStart(
        2,
        "0",
    )} ${suffix}`;
};

/* -------------------------------------------------------------------------- */

const formatOutletType = (type) => {
    if (type === "COMPANY_OWNED") {
        return "Company";
    }

    if (type === "FRANCHISE") {
        return "Franchise";
    }

    if (!type) {
        return "-";
    }

    return String(type)
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

/* -------------------------------------------------------------------------- */

const getOutletHours = (outlet) => {
    const opens = formatTime(outlet?.openingTime);
    const closes = formatTime(outlet?.closingTime);

    if (opens === "-" && closes === "-") {
        return "Hours not set";
    }

    return `${opens} – ${closes}`;
};

/* ==========================================================================
   COMPONENT
   ========================================================================== */

const OutletMapInsight = ({
    onStateClick,
    onOutletClick,
}) => {
    const mapWrapperRef = useRef(null);

    /* ======================================================================
       STATE
       ====================================================================== */

    const [outlets, setOutlets] = useState([]);

    const [salesByOutletId, setSalesByOutletId] =
        useState({});

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");

    /*
     * Map metric
     */

    const [dateFilter, setDateFilter] = useState("last30days");
    const [customStartDate, setCustomStartDate] = useState(getDefaultStartDate);
    const [customEndDate, setCustomEndDate] = useState(() => new Date());
    const [appliedCustomRange, setAppliedCustomRange] = useState(null);
    const [customDateError, setCustomDateError] = useState("");

    /*
     * Map state interaction
     */

    const [hoveredStateId, setHoveredStateId] =
        useState(null);

    const [selectedStateId, setSelectedStateId] =
        useState(null);

    /*
     * Right panel status filter
     */

    const [selectedStatus, setSelectedStatus] =
        useState("total");

    const selectedDateLabel =
        DATE_FILTER_OPTIONS.find((item) => item.value === dateFilter)?.label ||
        "Last 30 Days";

    /* ======================================================================
       API - OUTLETS
       ====================================================================== */

    const getOutlets = useCallback(async () => {
        const token =
            localStorage.getItem("authToken");

        const response = await axiosInstance.get("outlet", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (response?.data?.status !== true) {
            throw new Error(
                response?.data?.message ||
                "Unable to load outlets.",
            );
        }

        const allOutlets =
            response?.data?.data?.outlets || [];

        return allOutlets.filter(
            (outlet) =>
                outlet?.status === "ACTIVE" &&
                outlet?.isDelete !== true,
        );
    }, []);

    /* ======================================================================
       API - SALES
       ====================================================================== */

    const getOutletSales = useCallback(async () => {
        if (dateFilter === "custom" && !appliedCustomRange) {
            return {};
        }

        const token =
            localStorage.getItem("authToken");

        const dateQuery =
            dateFilter === "custom" && appliedCustomRange
                ? `startDate=${appliedCustomRange.start}&endDate=${appliedCustomRange.end}`
                : `dateFilter=${dateFilter}`;

        const fetchStats = async (filter) => {
            const response = await axiosInstance.get(
                `dashboard/getAllOutletRevenueStats?filter=${filter}&sort=-1&${dateQuery}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (response?.data?.status !== true) {
                return [];
            }

            return response?.data?.data?.data?.outletSales || [];
        };

        const [orderRows, salesRows] = await Promise.all([
            fetchStats("orders"),
            fetchStats("totalSales"),
        ]);

        const lookup = {};

        const applyRow = (item) => {
            const id =
                item?._id ||
                item?.outletId;

            if (!id) return;

            if (!lookup[id]) {
                lookup[id] = {
                    totalOrders: 0,
                    totalRevenue: 0,
                };
            }

            if (item?.totalOrders != null) {
                lookup[id].totalOrders =
                    Number(item.totalOrders) || 0;
            }

            if (item?.totalRevenue != null) {
                lookup[id].totalRevenue =
                    Number(item.totalRevenue) || 0;
            }
        };

        orderRows.forEach(applyRow);
        salesRows.forEach(applyRow);

        return lookup;
    }, [dateFilter, appliedCustomRange]);

    /* ======================================================================
       LOAD DATA
       ====================================================================== */

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const [
                outletList,
                salesLookup,
            ] = await Promise.all([
                getOutlets(),
                getOutletSales(),
            ]);

            setOutlets(outletList);

            setSalesByOutletId(salesLookup);
        } catch (error) {
            console.error(
                "Error fetching outlet map data:",
                error,
            );

            setOutlets([]);

            setSalesByOutletId({});

            setError(
                error?.message ||
                "Unable to load outlet information.",
            );
        } finally {
            setLoading(false);
        }
    }, [
        getOutlets,
        getOutletSales,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    /* ======================================================================
       PROCESS OUTLETS
       ====================================================================== */

    const processedOutlets = useMemo(() => {
        return outlets.map((outlet) => {
            const sales =
                salesByOutletId[outlet._id] || {};

            return {
                ...outlet,

                operationalStatus:
                    getOutletStatus(outlet),

                totalOrders: Number(sales.totalOrders) || 0,

                totalRevenue: Number(sales.totalRevenue) || 0,
            };
        });
    }, [
        outlets,
        salesByOutletId,
    ]);

    /* ======================================================================
       STATE-WISE DATA
       ====================================================================== */

    const stateStats = useMemo(() => {
        const grouped = {};

        processedOutlets.forEach((outlet) => {
            const stateName =
                outlet?.state?.trim();

            if (!stateName) return;

            const stateId =
                STATE_ID_MAP[stateName];

            if (!stateId) return;

            if (!grouped[stateId]) {
                grouped[stateId] = {
                    id: stateId,

                    state: stateName,

                    outletCount: 0,

                    totalOrders: 0,

                    totalRevenue: 0,

                    open: 0,

                    closed: 0,

                    temporarily_closed: 0,

                    outlets: [],
                };
            }

            const bucket =
                grouped[stateId];

            bucket.outletCount += 1;

            bucket.totalOrders += Number(outlet.totalOrders) || 0;

            bucket.totalRevenue += Number(outlet.totalRevenue) || 0;

            bucket[outlet.operationalStatus] =
                (
                    bucket[
                    outlet.operationalStatus
                    ] || 0
                ) + 1;

            bucket.outlets.push(outlet);
        });

        /*
         * Sort outlets inside each state.
         */

        Object.values(grouped).forEach(
            (bucket) => {
                bucket.outlets.sort((a, b) => {
                    const left = Number(a?.totalOrders) || 0;
                    const right = Number(b?.totalOrders) || 0;
                    return right - left;
                });
            },
        );

        return grouped;
    }, [
        processedOutlets,
    ]);

    /* ======================================================================
       METRIC HELPERS
       ====================================================================== */

    const getMetricValue = useCallback(
        (stats) => {
            return Number(stats?.totalOrders) || 0;
        },
        [],
    );

    /* ---------------------------------------------------------------------- */

    const maxMetricValue = useMemo(() => {
        const values =
            Object.values(stateStats).map(
                getMetricValue,
            );

        return values.length
            ? Math.max(...values)
            : 0;
    }, [
        stateStats,
        getMetricValue,
    ]);

    /* ---------------------------------------------------------------------- */

    const getHeatColor = useCallback(
        (value) => {
            if (
                !value ||
                maxMetricValue === 0
            ) {
                return HEAT_COLORS.empty;
            }

            const ratio =
                value / maxMetricValue;

            if (ratio <= 0.2) {
                return HEAT_COLORS.veryLow;
            }

            if (ratio <= 0.4) {
                return HEAT_COLORS.low;
            }

            if (ratio <= 0.6) {
                return HEAT_COLORS.medium;
            }

            if (ratio <= 0.8) {
                return HEAT_COLORS.high;
            }

            return HEAT_COLORS.veryHigh;
        },
        [maxMetricValue],
    );

    /* ---------------------------------------------------------------------- */

    const formatMetric = useCallback(
        (value) => {
            return formatOrders(value);
        },
        [],
    );

    /* ======================================================================
       MAP DATA
       ====================================================================== */

    const stateData = useMemo(() => {
        return Object.values(stateStats).map(
            (item) => ({
                id: item.id,

                customData: {
                    state:
                        item.state,

                    outletCount:
                        item.outletCount,

                    totalOrders: item.totalOrders,

                    totalRevenue: item.totalRevenue,
                },
            }),
        );
    }, [stateStats]);

    /* ======================================================================
       APPLY MAP COLORS
       ====================================================================== */

    useEffect(() => {
        if (
            !mapWrapperRef.current ||
            loading
        ) {
            return;
        }

        const timer = setTimeout(() => {
            const paths =
                mapWrapperRef.current.querySelectorAll(
                    "svg path",
                );

            paths.forEach((path) => {
                const stateId =
                    path.getAttribute("id") ||
                    path.getAttribute(
                        "data-id",
                    ) ||
                    path.getAttribute(
                        "data-state",
                    );

                if (!stateId) return;

                const value =
                    getMetricValue(
                        stateStats[stateId],
                    );

                const color =
                    getHeatColor(value);

                path.style.fill = color;

                path.style.stroke =
                    "#FFFFFF";

                path.style.strokeWidth =
                    "1.2";

                path.style.transition =
                    "fill .2s ease, opacity .2s ease, filter .2s ease";

                path.dataset.originalFill =
                    color;
            });
        }, 100);

        return () =>
            clearTimeout(timer);
    }, [
        stateStats,
        getHeatColor,
        getMetricValue,
        loading,
    ]);

    /* ======================================================================
       SELECTED / HOVERED STATE
       ====================================================================== */

    const activeStateId =
        hoveredStateId ||
        selectedStateId;

    const activeState =
        activeStateId
            ? stateStats[activeStateId]
            : null;

    const selectedState =
        selectedStateId
            ? stateStats[selectedStateId]
            : null;

    /* ======================================================================
       RIGHT PANEL BASE OUTLETS

       No state selected:
       -> all outlets

       State selected:
       -> outlets from selected state
       ====================================================================== */

    const rightPanelOutlets = useMemo(() => {
        if (selectedState) {
            return selectedState.outlets || [];
        }

        return processedOutlets;
    }, [
        selectedState,
        processedOutlets,
    ]);

    /* ======================================================================
       RIGHT PANEL COUNTS
       ====================================================================== */

    const rightPanelStats = useMemo(() => {
        const stats = {
            total: rightPanelOutlets.length,
            open: 0,
            closed: 0,
            temporarily_closed: 0,
        };

        rightPanelOutlets.forEach(
            (outlet) => {
                const status =
                    outlet.operationalStatus;

                if (
                    Object.prototype.hasOwnProperty.call(
                        stats,
                        status,
                    )
                ) {
                    stats[status] += 1;
                }
            },
        );

        return stats;
    }, [rightPanelOutlets]);

    /* ======================================================================
       FILTER RIGHT PANEL OUTLETS
       ====================================================================== */

    const filteredRightPanelOutlets =
        useMemo(() => {
            let list =
                [...rightPanelOutlets];

            if (
                selectedStatus !== "total"
            ) {
                list = list.filter(
                    (outlet) =>
                        outlet.operationalStatus ===
                        selectedStatus,
                );
            }

            /*
             * Keep list sorted using current map metric.
             */

            list.sort((a, b) => {
                const left = Number(a?.totalOrders) || 0;
                const right = Number(b?.totalOrders) || 0;
                return right - left;
            });

            return list;
        }, [
            rightPanelOutlets,
            selectedStatus,
        ]);

    /* ======================================================================
       MAP EVENTS
       ====================================================================== */

    const handleStateHover =
        (stateId) => {
            setHoveredStateId(stateId);
        };

    /* ---------------------------------------------------------------------- */

    const handleStateClick =
        (stateId, stateInfo) => {
            if (selectedStateId === stateId) {
                clearSelectedState();
                return;
            }

            const stats =
                stateStats[stateId];

            /*
             * Ignore states without outlet data.
             */

            if (!stats) {
                return;
            }

            setSelectedStateId(stateId);

            /*
             * Reset right panel to total
             * whenever state changes.
             */

            setSelectedStatus("total");

            if (onStateClick) {
                onStateClick({
                    ...stats,
                    stateInfo,
                });
            }
        };

    /* ---------------------------------------------------------------------- */

    const clearSelectedState = () => {
        setSelectedStateId(null);

        setHoveredStateId(null);

        setSelectedStatus("total");
    };

    /* ======================================================================
       LOADING
       ====================================================================== */

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{
                    minHeight: 500,
                }}
            >
                <Spinner
                    animation="border"
                    style={{
                        color: "#0EA5A4",
                    }}
                />
            </div>
        );
    }

    /* ======================================================================
       ERROR
       ====================================================================== */

    if (error) {
        return (
            <div
                className="outlet-map-error"
                style={{
                    minHeight: 400,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    color: "#667085",
                }}
            >
                <ShopWindow size={35} />

                <div
                    style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#344054",
                    }}
                >
                    {error}
                </div>

                <button
                    type="button"
                    onClick={loadData}
                    style={{
                        border: "1px solid #0EA5A4",
                        color: "#087C7A",
                        background: "#FFFFFF",
                        padding: "7px 14px",
                        borderRadius: 9,
                        fontSize: 12,
                        fontWeight: 700,
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    /* ======================================================================
       RENDER
       ====================================================================== */

    return (
        <div className="india-outlet-heatmap">

            {/* ==============================================================
                HEADER
                ============================================================== */}

            <div className="outlet-map-header">

                <div>
                    <div className="outlet-map-title">
                        State-wise Orders Across India
                    </div>

                    <div className="outlet-map-subtitle">
                        Heatmap of {selectedDateLabel.toLowerCase()}
                        {dateFilter === "custom" && appliedCustomRange
                            ? ` (${appliedCustomRange.start} to ${appliedCustomRange.end})`
                            : ""}{" "}
                        across active outlets
                    </div>
                </div>

                <div className="outlet-map-header-actions">
                    <Form.Select
                        className="heatmap-select"
                        value={dateFilter}
                        onChange={(event) => {
                            const value = event.target.value;
                            setDateFilter(value);
                            setCustomDateError("");

                            if (value === "custom") {
                                const start = getDefaultStartDate();
                                const end = new Date();
                                setCustomStartDate(start);
                                setCustomEndDate(end);
                                setAppliedCustomRange({
                                    start: toApiDate(start),
                                    end: toApiDate(end),
                                });
                            } else {
                                setAppliedCustomRange(null);
                            }
                        }}
                    >
                        {DATE_FILTER_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>

                    <div className="active-outlet-pill">
                        {processedOutlets.length}{" "}
                        Active Outlets
                    </div>
                </div>

            </div>

            {dateFilter === "custom" && (
                <div className="heatmap-custom-range">
                    <div>
                        <span>Start</span>
                        <DatePicker
                            selected={customStartDate}
                            onChange={(date) => {
                                setCustomStartDate(date);
                                if (customEndDate && date) {
                                    const diff = Math.ceil(
                                        (customEndDate - date) / (1000 * 60 * 60 * 24),
                                    );
                                    if (diff > 92 || diff < 0) setCustomEndDate(null);
                                }
                                setCustomDateError("");
                            }}
                            selectsStart
                            startDate={customStartDate}
                            endDate={customEndDate}
                            minDate={new Date(new Date().setDate(new Date().getDate() - 92))}
                            maxDate={new Date()}
                            dateFormat="dd-MM-yyyy"
                            placeholderText="Start date"
                            className="form-control form-control-sm heatmap-date-input"
                        />
                    </div>

                    <div>
                        <span>End</span>
                        <DatePicker
                            selected={customEndDate}
                            onChange={(date) => {
                                setCustomEndDate(date);
                                setCustomDateError("");
                            }}
                            selectsEnd
                            startDate={customStartDate}
                            endDate={customEndDate}
                            minDate={
                                customStartDate ||
                                new Date(new Date().setDate(new Date().getDate() - 90))
                            }
                            maxDate={
                                customStartDate
                                    ? new Date(
                                          Math.min(
                                              Date.now(),
                                              new Date(customStartDate).setDate(
                                                  customStartDate.getDate() + 92,
                                              ),
                                          ),
                                      )
                                    : new Date()
                            }
                            dateFormat="dd-MM-yyyy"
                            placeholderText="End date"
                            disabled={!customStartDate}
                            className="form-control form-control-sm heatmap-date-input"
                        />
                    </div>

                    <button
                        type="button"
                        className="heatmap-apply-button"
                        disabled={!customStartDate || !customEndDate}
                        onClick={() => {
                            if (!customStartDate || !customEndDate) {
                                setCustomDateError("Please select both dates.");
                                return;
                            }

                            const diffDays = Math.ceil(
                                (customEndDate - customStartDate) / (1000 * 60 * 60 * 24),
                            );

                            if (diffDays > 92) {
                                setCustomDateError("Range cannot exceed 92 days.");
                                return;
                            }

                            setCustomDateError("");
                            setAppliedCustomRange({
                                start: toApiDate(customStartDate),
                                end: toApiDate(customEndDate),
                            });
                        }}
                    >
                        Apply
                    </button>

                    {customDateError && (
                        <span className="heatmap-range-error">{customDateError}</span>
                    )}
                </div>
            )}

            {/* ==============================================================
                MAIN GRID
                ============================================================== */}

            <div className="row g-3">

                {/* ==========================================================
                    LEFT - MAP
                    ========================================================== */}

                <div className="col-12 col-xl-8">

                    <div className="india-heatmap-card">

                        <div className="india-heatmap-map-wrapper">

                            {/* ==================================================
                                HOVER INFO
                                ================================================== */}

                            {activeState && (
                                <div className="heatmap-info-card">

                                    <div className="heatmap-info-label">
                                        State
                                    </div>

                                    <div className="heatmap-info-title">
                                        {activeState.state}
                                    </div>

                                    <div className="heatmap-info-metrics">

                                        <div className="heatmap-info-metric">

                                            <div
                                                className="heatmap-info-color"
                                                style={{
                                                    background:
                                                        getHeatColor(
                                                            getMetricValue(
                                                                activeState,
                                                            ),
                                                        ),
                                                }}
                                            />

                                            <span>
                                                <strong>
                                                    {formatMetric(
                                                        getMetricValue(
                                                            activeState,
                                                        ),
                                                    )}
                                                </strong>{" "}
                                                orders
                                            </span>

                                        </div>

                                        <div className="heatmap-info-metric">
                                            <div className="heatmap-info-color heatmap-info-color-spacer" />
                                            <span>
                                                <strong>
                                                    {formatRevenue(
                                                        activeState.totalRevenue,
                                                    )}
                                                </strong>{" "}
                                                revenue
                                            </span>
                                        </div>

                                    </div>

                                    <div className="heatmap-info-status">
                                        {
                                            activeState.outletCount
                                        }{" "}
                                        outlet
                                        {activeState.outletCount ===
                                            1
                                            ? ""
                                            : "s"}
                                        {" · "}
                                        {activeState.open} open
                                        {" · "}
                                        {
                                            activeState.closed
                                        }{" "}
                                        closed

                                        {activeState.temporarily_closed >
                                            0 &&
                                            ` · ${activeState.temporarily_closed} temp.`}
                                    </div>

                                    <div className="heatmap-info-hint">
                                        {selectedStateId ===
                                        activeState.id
                                            ? "Click again to unselect"
                                            : "Click state to view outlets"}
                                    </div>
                                </div>
                            )}

                            {/* ==================================================
                                MAP
                                ================================================== */}

                            <div
                                ref={
                                    mapWrapperRef
                                }
                                className="india-heatmap-map"
                                onClick={(event) => {
                                    if (!selectedStateId) {
                                        return;
                                    }

                                    if (event.target.closest("path")) {
                                        return;
                                    }

                                    clearSelectedState();
                                }}
                            >
                                <IndiaMap
                                    stateData={
                                        stateData
                                    }
                                    onStateHover={
                                        handleStateHover
                                    }
                                    onStateClick={
                                        handleStateClick
                                    }
                                    mapStyle={{
                                        backgroundColor:
                                            HEAT_COLORS.empty,

                                        hoverColor:
                                            "#FFB86B",

                                        stroke:
                                            "#FFFFFF",

                                        strokeWidth:
                                            1,

                                        tooltipConfig:
                                        {
                                            backgroundColor:
                                                "rgba(15, 23, 42, .94)",

                                            textColor:
                                                "#FFFFFF",
                                        },
                                    }}
                                />
                            </div>

                            {/* ==================================================
                                LEGEND
                                ================================================== */}

                            <div className="heatmap-legend">

                                <span>
                                    Lower
                                </span>

                                {[
                                    HEAT_COLORS.veryLow,
                                    HEAT_COLORS.low,
                                    HEAT_COLORS.medium,
                                    HEAT_COLORS.high,
                                    HEAT_COLORS.veryHigh,
                                ].map(
                                    (color) => (
                                        <div
                                            key={
                                                color
                                            }
                                            className="heat-legend-item"
                                            style={{
                                                background:
                                                    color,
                                            }}
                                        />
                                    ),
                                )}

                                <span>
                                    Higher
                                </span>

                            </div>

                        </div>

                    </div>

                </div>

                {/* ==========================================================
                    RIGHT - OUTLET INSIGHT
                    ========================================================== */}

                <div className="col-12 col-xl-4">

                    <div className="outlet-insight-panel">

                        {/* ==================================================
                            PANEL HEADER
                            ================================================== */}

                        <div className="outlet-insight-header">

                            <div className="outlet-insight-heading">

                                <div className="outlet-insight-main-icon">
                                    <ShopWindow />
                                </div>

                                <div>
                                    <div className="outlet-insight-title">
                                        Outlet Status
                                    </div>

                                    <div className="outlet-insight-description">
                                        {selectedState
                                            ? `${selectedState.state} outlets`
                                            : "All active outlets"}
                                    </div>
                                </div>

                            </div>

                            {selectedState && (
                                <button
                                    type="button"
                                    className="outlet-state-clear"
                                    onClick={
                                        clearSelectedState
                                    }
                                    title="Show all India"
                                >
                                    <ArrowLeft />
                                </button>
                            )}

                        </div>

                        {/* ==================================================
                            SELECTED STATE
                            ================================================== */}

                        {selectedState && (
                            <div className="selected-state-banner">

                                <div className="selected-state-icon">
                                    <GeoAltFill />
                                </div>

                                <div>
                                    <strong>
                                        {
                                            selectedState.state
                                        }
                                    </strong>

                                    <span>
                                        {
                                            selectedState.outletCount
                                        }{" "}
                                        active outlet
                                        {selectedState.outletCount ===
                                            1
                                            ? ""
                                            : "s"}
                                    </span>
                                </div>

                                <div className="selected-state-metric">
                                    {formatMetric(
                                        getMetricValue(
                                            selectedState,
                                        ),
                                    )}

                                    <small>
                                        Orders
                                    </small>
                                </div>

                                <button
                                    type="button"
                                    className="selected-state-clear"
                                    onClick={
                                        clearSelectedState
                                    }
                                    title="Show all India"
                                >
                                    Clear
                                </button>

                            </div>
                        )}

                        {/* ==================================================
                            KPI GRID
                            ================================================== */}

                        <div className="outlet-insight-stats">

                            {/* TOTAL */}

                            <button
                                type="button"
                                className={`outlet-insight-stat total ${selectedStatus ===
                                        "total"
                                        ? "active"
                                        : ""
                                    }`}
                                onClick={() =>
                                    setSelectedStatus(
                                        "total",
                                    )
                                }
                            >

                                <div className="outlet-insight-stat-icon">
                                    <Shop />
                                </div>

                                <div>
                                    <strong>
                                        {
                                            rightPanelStats.total
                                        }
                                    </strong>

                                    <span>
                                        Total
                                    </span>
                                </div>

                                <ChevronRight className="outlet-insight-arrow" />

                            </button>

                            {/* OPEN */}

                            <button
                                type="button"
                                className={`outlet-insight-stat open ${selectedStatus ===
                                        "open"
                                        ? "active"
                                        : ""
                                    }`}
                                onClick={() =>
                                    setSelectedStatus(
                                        "open",
                                    )
                                }
                            >

                                <div className="outlet-insight-stat-icon">
                                    <CheckCircleFill />
                                </div>

                                <div>
                                    <strong>
                                        {
                                            rightPanelStats.open
                                        }
                                    </strong>

                                    <span>
                                        Open
                                    </span>
                                </div>

                                <ChevronRight className="outlet-insight-arrow" />

                            </button>

                            {/* CLOSED */}

                            <button
                                type="button"
                                className={`outlet-insight-stat closed ${selectedStatus ===
                                        "closed"
                                        ? "active"
                                        : ""
                                    }`}
                                onClick={() =>
                                    setSelectedStatus(
                                        "closed",
                                    )
                                }
                            >

                                <div className="outlet-insight-stat-icon">
                                    <XCircleFill />
                                </div>

                                <div>
                                    <strong>
                                        {
                                            rightPanelStats.closed
                                        }
                                    </strong>

                                    <span>
                                        Closed
                                    </span>
                                </div>

                                <ChevronRight className="outlet-insight-arrow" />

                            </button>

                            {/* TEMP CLOSED */}

                            <button
                                type="button"
                                className={`outlet-insight-stat temporary ${selectedStatus ===
                                        "temporarily_closed"
                                        ? "active"
                                        : ""
                                    }`}
                                onClick={() =>
                                    setSelectedStatus(
                                        "temporarily_closed",
                                    )
                                }
                            >

                                <div className="outlet-insight-stat-icon">
                                    <ClockFill />
                                </div>

                                <div>
                                    <strong>
                                        {
                                            rightPanelStats.temporarily_closed
                                        }
                                    </strong>

                                    <span>
                                        Temp. Closed
                                    </span>
                                </div>

                                <ChevronRight className="outlet-insight-arrow" />

                            </button>

                        </div>

                        {/* ==================================================
                            LIST HEADER
                            ================================================== */}

                        <div className="outlet-list-header">

                            <div>
                                {selectedStatus ===
                                    "total"
                                    ? "All Outlets"
                                    : selectedStatus ===
                                        "open"
                                        ? "Open Outlets"
                                        : selectedStatus ===
                                            "closed"
                                            ? "Closed Outlets"
                                            : "Temporarily Closed"}
                            </div>

                            <span>
                                {
                                    filteredRightPanelOutlets.length
                                }
                            </span>

                        </div>

                        {/* ==================================================
                            OUTLET LIST
                            ================================================== */}

                        <div className="outlet-insight-list">

                            {filteredRightPanelOutlets.length >
                                0 ? (
                                filteredRightPanelOutlets.map(
                                    (outlet) => {
                                        const status =
                                            STATUS_META[
                                            outlet
                                                .operationalStatus
                                            ] ||
                                            STATUS_META.closed;

                                        return (
                                            <div
                                                key={
                                                    outlet._id
                                                }
                                                className={`outlet-insight-item ${status.className}`}
                                                onClick={() =>
                                                    onOutletClick?.(
                                                        outlet,
                                                    )
                                                }
                                            >

                                                {/* IMAGE */}

                                                <div className="outlet-insight-image">

                                                    {outlet?.outletImage ? (
                                                        <img
                                                            src={
                                                                outlet.outletImage
                                                            }
                                                            alt={
                                                                outlet?.name ||
                                                                "Outlet"
                                                            }
                                                        />
                                                    ) : (
                                                        <ShopWindow />
                                                    )}

                                                </div>

                                                {/* CONTENT */}

                                                <div className="outlet-insight-item-content">

                                                    <div className="outlet-insight-name-row">

                                                        <div className="outlet-insight-name">
                                                            {outlet?.name ||
                                                                "Unnamed Outlet"}
                                                        </div>

                                                        <OutletStatusBadge
                                                            status={
                                                                outlet.operationalStatus
                                                            }
                                                        />

                                                    </div>

                                                    {/* META */}

                                                    <div className="outlet-insight-meta">

                                                        <div className="outlet-meta-chip">
                                                            <Clock
                                                                size={
                                                                    11
                                                                }
                                                            />

                                                            <span>
                                                                {getOutletHours(
                                                                    outlet,
                                                                )}
                                                            </span>
                                                        </div>

                                                        <div className="outlet-meta-chip">
                                                            <Shop
                                                                size={
                                                                    11
                                                                }
                                                            />

                                                            <span>
                                                                {formatOutletType(
                                                                    outlet?.type,
                                                                )}
                                                            </span>
                                                        </div>

                                                        {outlet?.phone && (
                                                            <div className="outlet-meta-chip">
                                                                <TelephoneFill
                                                                    size={
                                                                        10
                                                                    }
                                                                />

                                                                <span>
                                                                    {
                                                                        outlet.phone
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}

                                                    </div>

                                                    {/* TEMP CLOSED MESSAGE */}

                                                    {outlet.operationalStatus ===
                                                        "temporarily_closed" &&
                                                        outlet?.unavailabilityMessage && (
                                                            <div className="outlet-temp-message">
                                                                {
                                                                    outlet.unavailabilityMessage
                                                                }
                                                            </div>
                                                        )}

                                                </div>

                                            </div>
                                        );
                                    },
                                )
                            ) : (
                                <div className="outlet-insight-empty">

                                    <ShopWindow />

                                    <strong>
                                        No outlets found
                                    </strong>

                                    <span>
                                        No outlets match
                                        this filter.
                                    </span>

                                </div>
                            )}

                        </div>

                    </div>

                </div>

            </div>

            {/* ==============================================================
                STYLES
                ============================================================== */}

            <style>
                {`

                /* ==========================================================
                   HEADER
                ========================================================== */

                .outlet-map-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                    margin-bottom: 16px;
                }

                .outlet-map-title {
                    font-size: 16px;
                    font-weight: 900;
                    color: #172126;
                }

                .outlet-map-subtitle {
                    font-size: 12px;
                    color: #819093;
                    margin-top: 3px;
                }

                .outlet-map-header-actions {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 8px;
                }

                .active-outlet-pill {
                    padding: 7px 12px;
                    border-radius: 10px;
                    background: #EAF7F7;
                    color: #087C7A;
                    font-size: 12px;
                    font-weight: 900;
                }


                /* ==========================================================
                   MAIN CARD
                ========================================================== */

                .india-heatmap-card,
                .outlet-insight-panel {
                    background: #FFFFFF;
                    border: 1px solid #E4ECEE;
                    border-radius: 18px;
                }

                .india-heatmap-card {
                    overflow: hidden;
                    height: 100%;
                }


                /* ==========================================================
                   MAP
                ========================================================== */

                .india-heatmap-map-wrapper {
                    position: relative;
                    min-height: 610px;
                }

                .india-heatmap-map {
                    max-width: 650px;
                    margin: auto;
                    padding: 20px 10px 5px;
                }

                .india-heatmap-map svg {
                    width: 100% !important;
                    height: auto !important;
                }

                .india-heatmap-map svg path {
                    cursor: pointer;
                }

                .india-heatmap-map svg path:hover {
                    opacity: .82;
                    filter:
                        drop-shadow(
                            0 3px 4px
                            rgba(15,23,42,.18)
                        );
                }


                .heatmap-select {
                    width: auto !important;
                    min-width: 148px;
                    height: 38px;
                    border-color: #D7E8E8 !important;
                    border-radius: 11px !important;
                    color: #334155 !important;
                    font-size: 12px !important;
                    font-weight: 800;
                    box-shadow: none !important;
                }

                .heatmap-custom-range {
                    display: flex;
                    align-items: flex-end;
                    flex-wrap: wrap;
                    gap: 10px;
                    margin: -4px 0 16px;
                }

                .heatmap-custom-range span {
                    display: block;
                    font-size: 10px;
                    font-weight: 800;
                    color: #94A3B8;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    margin-bottom: 4px;
                }

                .heatmap-date-input {
                    height: 38px;
                    min-width: 140px;
                    border: 1px solid #D7E8E8;
                    border-radius: 11px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #334155;
                    padding: 0 12px;
                }

                .heatmap-apply-button {
                    height: 38px;
                    border: 0;
                    border-radius: 11px;
                    background: #0EA5A4;
                    color: #FFFFFF;
                    padding: 0 16px;
                    font-size: 12px;
                    font-weight: 800;
                    cursor: pointer;
                }

                .heatmap-apply-button:disabled {
                    opacity: .5;
                    cursor: not-allowed;
                }

                .heatmap-range-error {
                    color: #D92D45;
                    font-size: 12px;
                    font-weight: 700;
                    align-self: center;
                }


                /* ==========================================================
                   MAP INFO
                ========================================================== */

                .heatmap-info-card {
                    position: absolute;
                    top: 18px;
                    left: 18px;
                    width: 235px;
                    padding: 14px 16px;
                    background:
                        rgba(255,255,255,.96);
                    backdrop-filter: blur(10px);
                    border: 1px solid #E5EDEF;
                    border-radius: 14px;
                    box-shadow:
                        0 10px 30px
                        rgba(15,23,42,.08);
                    z-index: 20;
                    pointer-events: none;
                }

                .heatmap-info-label {
                    font-size: 10px;
                    font-weight: 800;
                    color: #94A3B8;
                    text-transform: uppercase;
                    letter-spacing: .5px;
                }

                .heatmap-info-title {
                    font-size: 17px;
                    font-weight: 900;
                    color: #172126;
                    margin-top: 2px;
                }

                .heatmap-info-metrics {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    margin-top: 9px;
                }

                .heatmap-info-metric {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12px;
                    color: #64748B;
                }

                .heatmap-info-metric strong {
                    color: #172126;
                }

                .heatmap-info-color {
                    width: 10px;
                    height: 10px;
                    border-radius: 3px;
                    flex-shrink: 0;
                }

                .heatmap-info-color-spacer {
                    background: transparent;
                }

                .heatmap-info-status {
                    font-size: 10px;
                    color: #94A3B8;
                    margin-top: 8px;
                    line-height: 1.5;
                }

                .heatmap-info-hint {
                    margin-top: 9px;
                    padding-top: 8px;
                    border-top: 1px solid #EEF2F3;
                    font-size: 9px;
                    color: #A0ADB0;
                }


                /* ==========================================================
                   LEGEND
                ========================================================== */

                .heatmap-legend {
                    position: absolute;
                    top: 18px;
                    right: 18px;
                    z-index: 20;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 6px;
                    // max-width: 220px;
                    padding: 10px 12px;
                    background: rgba(255,255,255,.96);
                    backdrop-filter: blur(10px);
                    border: 1px solid #E5EDEF;
                    border-radius: 14px;
                    // box-shadow: 0 10px 30px rgba(15,23,42,.08);
                    pointer-events: none;
                }

                .heatmap-legend span {
                    font-size: 10px;
                    color: #94A3B8;
                    margin: 0 3px;
                }

                .heat-legend-item {
                    width: 26px;
                    height: 9px;
                    border-radius: 20px;
                }


                /* ==========================================================
                   RIGHT PANEL
                ========================================================== */

                .outlet-insight-panel {
                    height: 100%;
                    max-height: 680px;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }


                /* ==========================================================
                   RIGHT HEADER
                ========================================================== */

                .outlet-insight-header {
                    padding: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    border-bottom: 1px solid #EDF2F3;
                    flex-shrink: 0;
                }

                .outlet-insight-heading {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                }

                .outlet-insight-main-icon {
                    width: 40px;
                    height: 40px;
                    min-width: 40px;
                    border-radius: 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background:
                        linear-gradient(
                            135deg,
                            #E7F7F6,
                            #DDF3F2
                        );
                    color: #0E9996;
                    font-size: 19px;
                }

                .outlet-insight-title {
                    color: #172126;
                    font-size: 14px;
                    font-weight: 900;
                }

                .outlet-insight-description {
                    color: #94A3B8;
                    font-size: 10px;
                    margin-top: 2px;
                }

                .outlet-state-clear {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    border-radius: 9px;
                    border: 1px solid #DFE7E9;
                    background: #FFFFFF;
                    color: #64748B;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }

                .outlet-state-clear:hover {
                    border-color: #0EA5A4;
                    color: #087C7A;
                    background: #F6FCFC;
                }


                /* ==========================================================
                   SELECTED STATE
                ========================================================== */

                .selected-state-banner {
                    margin: 12px 14px 0;
                    padding: 10px 12px;
                    border-radius: 11px;
                    background:
                        linear-gradient(
                            135deg,
                            #F1FAFA,
                            #F8FDFD
                        );
                    border: 1px solid #D9EEEE;
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    flex-shrink: 0;
                }

                .selected-state-icon {
                    width: 30px;
                    height: 30px;
                    min-width: 30px;
                    border-radius: 8px;
                    background: #DDF3F2;
                    color: #0E9996;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                }

                .selected-state-banner > div:nth-child(2) {
                    flex: 1;
                    min-width: 0;
                }

                .selected-state-banner strong {
                    display: block;
                    font-size: 11px;
                    color: #334155;
                    font-weight: 800;
                }

                .selected-state-banner span {
                    display: block;
                    margin-top: 1px;
                    font-size: 9px;
                    color: #94A3B8;
                }

                .selected-state-metric {
                    text-align: right;
                    color: #087C7A;
                    font-size: 11px;
                    font-weight: 900;
                    white-space: nowrap;
                }

                .selected-state-metric small {
                    display: block;
                    font-size: 8px;
                    color: #94A3B8;
                    font-weight: 600;
                    margin-top: 1px;
                }

                .selected-state-clear {
                    border: 1px solid #D9EEEE;
                    background: #FFFFFF;
                    color: #087C7A;
                    border-radius: 8px;
                    padding: 5px 8px;
                    font-size: 10px;
                    font-weight: 800;
                    cursor: pointer;
                    flex-shrink: 0;
                }

                .selected-state-clear:hover {
                    border-color: #0EA5A4;
                    background: #F6FCFC;
                }


                /* ==========================================================
                   KPI GRID
                ========================================================== */

                .outlet-insight-stats {
                    display: grid;
                    grid-template-columns:
                        repeat(2, minmax(0, 1fr));
                    gap: 8px;
                    padding: 12px 14px;
                    flex-shrink: 0;
                }

                .outlet-insight-stat {
                    position: relative;
                    min-width: 0;
                    min-height: 62px;
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    text-align: left;
                    padding: 9px 25px 9px 9px;
                    border-radius: 11px;
                    border: 1px solid transparent;
                    cursor: pointer;
                    font-family: inherit;
                    transition:
                        transform .15s ease,
                        border-color .15s ease,
                        box-shadow .15s ease;
                }

                .outlet-insight-stat:hover {
                    transform: translateY(-1px);
                    box-shadow:
                        0 5px 12px
                        rgba(15,23,42,.05);
                }

                .outlet-insight-stat.active {
                    box-shadow:
                        0 0 0 2px
                        rgba(14,165,164,.09);
                }

                .outlet-insight-stat-icon {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                }

                .outlet-insight-stat strong {
                    display: block;
                    font-size: 18px;
                    line-height: 1;
                    color: #101828;
                    font-weight: 850;
                }

                .outlet-insight-stat span {
                    display: block;
                    margin-top: 4px;
                    font-size: 9px;
                    color: #667085;
                    font-weight: 700;
                    white-space: nowrap;
                }

                .outlet-insight-arrow {
                    position: absolute;
                    right: 8px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #98A2B3;
                    font-size: 10px;
                }


                /* TOTAL */

                .outlet-insight-stat.total {
                    background: #F8F8FB;
                    border-color: #E8E8F0;
                }

                .outlet-insight-stat.total.active {
                    border-color: #AAAAC1;
                }

                .outlet-insight-stat.total
                .outlet-insight-stat-icon {
                    background: #EEEEF7;
                    color: #56627A;
                }


                /* OPEN */

                .outlet-insight-stat.open {
                    background: #F3FCF9;
                    border-color: #D7F2E8;
                }

                .outlet-insight-stat.open.active {
                    border-color: #75D0B1;
                }

                .outlet-insight-stat.open
                .outlet-insight-stat-icon {
                    background: #DDF8EF;
                    color: #0DB785;
                }


                /* CLOSED */

                .outlet-insight-stat.closed {
                    background: #FFF7F8;
                    border-color: #F9DDE1;
                }

                .outlet-insight-stat.closed.active {
                    border-color: #F0AAB4;
                }

                .outlet-insight-stat.closed
                .outlet-insight-stat-icon {
                    background: #FFE1E6;
                    color: #EF334E;
                }

                .outlet-insight-stat.closed strong {
                    color: #D92D45;
                }


                /* TEMP */

                .outlet-insight-stat.temporary {
                    background: #FFFAF3;
                    border-color: #FBE8C9;
                }

                .outlet-insight-stat.temporary.active {
                    border-color: #EDC57F;
                }

                .outlet-insight-stat.temporary
                .outlet-insight-stat-icon {
                    background: #FFF0D5;
                    color: #F79009;
                }


                /* ==========================================================
                   LIST HEADER
                ========================================================== */

                .outlet-list-header {
                    padding: 10px 15px 8px;
                    border-top: 1px solid #EDF2F3;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-shrink: 0;
                    font-size: 10px;
                    font-weight: 800;
                    color: #64748B;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                }

                .outlet-list-header span {
                    min-width: 24px;
                    height: 20px;
                    padding: 0 6px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #EEF5F5;
                    color: #64748B;
                    font-size: 9px;
                }


                /* ==========================================================
                   OUTLET LIST
                ========================================================== */

                .outlet-insight-list {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    padding: 0 12px 12px;
                }

                .outlet-insight-list::-webkit-scrollbar {
                    width: 5px;
                }

                .outlet-insight-list::-webkit-scrollbar-track {
                    background: transparent;
                }

                .outlet-insight-list::-webkit-scrollbar-thumb {
                    background: #DCE7E8;
                    border-radius: 10px;
                }


                /* ==========================================================
                   OUTLET ITEM
                ========================================================== */

                .outlet-insight-item {
                    display: flex;
                    gap: 10px;
                    padding: 10px;
                    margin-bottom: 7px;
                    background: #FFFFFF;
                    border: 1px solid #EDF2F3;
                    border-left: 3px solid #AAB7BA;
                    border-radius: 11px;
                    cursor: pointer;
                    transition:
                        background .15s ease,
                        border-color .15s ease,
                        transform .15s ease;
                }

                .outlet-insight-item:hover {
                    transform: translateY(-1px);
                    background: #FAFDFD;
                    border-color: #CBE5E4;
                }

                .outlet-insight-item.open {
                    border-left-color: #0DB785;
                }

                .outlet-insight-item.closed {
                    border-left-color: #EF334E;
                }

                .outlet-insight-item.temporary {
                    border-left-color: #F79009;
                }


                /* ==========================================================
                   OUTLET IMAGE
                ========================================================== */

                .outlet-insight-image {
                    width: 48px;
                    height: 48px;
                    min-width: 48px;
                    border-radius: 10px;
                    overflow: hidden;
                    background: #EAF7F7;
                    border: 1px solid #DFEEEE;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #0EA5A4;
                    font-size: 18px;
                }

                .outlet-insight-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }


                /* ==========================================================
                   OUTLET CONTENT
                ========================================================== */

                .outlet-insight-item-content {
                    flex: 1;
                    min-width: 0;
                }

                .outlet-insight-name-row {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 7px;
                }

                .outlet-insight-name {
                    min-width: 0;
                    color: #334155;
                    font-size: 11px;
                    line-height: 1.3;
                    font-weight: 850;
                }


                /* ==========================================================
                   STATUS BADGE
                ========================================================== */

                .outlet-list-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 3px;
                    padding: 3px 6px;
                    border-radius: 20px;
                    font-size: 8px;
                    line-height: 1;
                    font-weight: 800;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .outlet-list-status.open {
                    background: #E6F9F2;
                    color: #087A5B;
                }

                .outlet-list-status.closed {
                    background: #FFF0F1;
                    color: #D92D45;
                }

                .outlet-list-status.temporary {
                    background: #FFF5E4;
                    color: #B95F00;
                }


                /* ==========================================================
                   SALES
                ========================================================== */

                .outlet-insight-sales {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 4px;
                    margin-top: 4px;
                    font-size: 9px;
                    color: #94A3B8;
                }

                .outlet-insight-sales strong {
                    color: #087C7A;
                    font-size: 10px;
                    font-weight: 850;
                }

                .outlet-insight-sales i {
                    width: 3px;
                    height: 3px;
                    border-radius: 50%;
                    background: #CBD5E1;
                    margin: 0 2px;
                }


                /* ==========================================================
                   META
                ========================================================== */

                .outlet-insight-meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 4px;
                    margin-top: 6px;
                }

                .outlet-meta-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    min-height: 22px;
                    padding: 3px 6px;
                    border-radius: 6px;
                    background: #F7FAFA;
                    border: 1px solid #EBF0F1;
                    color: #64748B;
                    font-size: 8px;
                    font-weight: 650;
                }

                .outlet-meta-chip svg {
                    color: #8CA3A5;
                    flex-shrink: 0;
                }


                /* ==========================================================
                   TEMP MESSAGE
                ========================================================== */

                .outlet-temp-message {
                    margin-top: 6px;
                    padding: 5px 7px;
                    border-radius: 6px;
                    background: #FFF7E9;
                    border: 1px solid #F7E2B8;
                    color: #A85B0C;
                    font-size: 8px;
                    line-height: 1.4;
                    font-weight: 600;
                }


                /* ==========================================================
                   EMPTY
                ========================================================== */

                .outlet-insight-empty {
                    min-height: 180px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    color: #A4B0B3;
                    text-align: center;
                }

                .outlet-insight-empty > svg {
                    font-size: 28px;
                    margin-bottom: 3px;
                }

                .outlet-insight-empty strong {
                    color: #475569;
                    font-size: 12px;
                }

                .outlet-insight-empty span {
                    font-size: 10px;
                }


                /* ==========================================================
                   TABLET
                ========================================================== */

                @media(max-width: 1199px) {

                    .outlet-insight-panel {
                        max-height: none;
                    }

                    .outlet-insight-list {
                        max-height: 500px;
                    }

                    .india-heatmap-map-wrapper {
                        min-height: auto;
                    }

                }


                /* ==========================================================
                   MOBILE
                ========================================================== */

                @media(max-width: 767px) {

                    .outlet-map-header {
                        align-items: stretch;
                    }

                    .outlet-map-header-actions {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .heatmap-select {
                        flex: 1 1 140px;
                        min-width: 0;
                    }

                    .heatmap-info-card {
                        position: relative;
                        top: auto;
                        left: auto;
                        width: auto;
                        margin: 12px 12px 0;
                    }

                    .heatmap-legend {
                        position: relative;
                        top: auto;
                        right: auto;
                        max-width: none;
                        margin: 0 12px 12px auto;
                    }

                    .india-heatmap-map {
                        padding-top: 5px;
                    }

                    .outlet-insight-stats {
                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0, 1fr)
                            );
                    }

                }


                /* ==========================================================
                   SMALL MOBILE
                ========================================================== */

                @media(max-width: 480px) {

                    .outlet-insight-stats {
                        gap: 6px;
                        padding: 10px;
                    }

                    .outlet-insight-stat {
                        min-height: 58px;
                        padding-left: 7px;
                    }

                    .outlet-insight-stat-icon {
                        width: 29px;
                        height: 29px;
                        min-width: 29px;
                        font-size: 13px;
                    }

                    .outlet-insight-stat strong {
                        font-size: 16px;
                    }

                    .outlet-insight-stat span {
                        font-size: 8px;
                    }

                    .outlet-insight-item {
                        padding: 8px;
                    }

                    .outlet-insight-image {
                        width: 42px;
                        height: 42px;
                        min-width: 42px;
                    }

                }

            `}
            </style>

        </div>
    );
};

/* ==========================================================================
   OUTLET STATUS BADGE
   ========================================================================== */

const OutletStatusBadge = ({ status }) => {
    if (status === "open") {
        return (
            <div className="outlet-list-status open">
                <CheckCircleFill />
                Open
            </div>
        );
    }

    if (status === "temporarily_closed") {
        return (
            <div className="outlet-list-status temporary">
                <ClockFill />
                Temp.
            </div>
        );
    }

    return (
        <div className="outlet-list-status closed">
            <XCircleFill />
            Closed
        </div>
    );
};

export default OutletMapInsight;